import { ImagePreview } from "@opencode-ai/ui/image-preview"
import { FileIcon } from "@opencode-ai/ui/file-icon"
import { useDialog } from "@opencode-ai/ui/context/dialog"
import { ProviderIcon } from "@opencode-ai/ui/provider-icon"
import { ButtonV2 } from "@opencode-ai/ui/v2/button-v2"
import { DialogBody, DialogHeader, DialogTitle, DialogV2 } from "@opencode-ai/ui/v2/dialog-v2"
import { Icon } from "@opencode-ai/ui/v2/icon"
import { KeybindV2 } from "@opencode-ai/ui/v2/keybind-v2"
import { MenuV2 } from "@opencode-ai/ui/v2/menu-v2"
import { TooltipV2 } from "@opencode-ai/ui/v2/tooltip-v2"
import type { ReferenceInfo } from "@opencode-ai/sdk/v2/client"
import { createEffect, createMemo, createResource, For, on, onCleanup, onMount, Show, untrack } from "solid-js"
import { createStore } from "solid-js/store"
import { Portal } from "solid-js/web"
import { storedDefaultAgentID } from "@/components/settings-v2/cowork-agents"
import { CoworkReportTemplateCatalog, type CoworkReportTemplate } from "@/components/cowork-report-template-catalog"
import { ModelSelectorPopoverV2 } from "@/components/dialog-select-model"
import { DialogSelectModelUnpaidV2 } from "@/components/dialog-select-model-unpaid-v2"
import {
  coworkAgentMode,
  getFile,
  getSessionContextByRemote,
  importIntegrationFiles,
  listFiles,
  listIntegrationConnections,
  listIntegrationFiles,
  listWorkspaceAgents,
  uploadFile,
  type CoworkCloudFile,
  type CoworkIntegrationConnection,
} from "@/cowork/api"
import {
  activeRuntimeAgentOptions,
  runtimeAgentOption,
  runtimeAgentOptionFromMetadata,
} from "@/cowork/agent-runtime"
import {
  advanceReportTemplate,
  createReportTemplateAction,
  injectReportTemplateContext,
  matchingReportTemplate,
  reportTemplateMemory,
  type ReportTemplateAction,
} from "@/cowork/report-template-request"
import {
  buildCoworkReportTemplatePrompt,
  coworkReportOutputLanguages,
  coworkReportOutputLanguageInstructionValue,
  coworkReportOutputLanguageLabel,
  setStoredCoworkReportOutputLanguage,
  storedCoworkReportOutputLanguage,
  type CoworkReportOutputLanguage,
} from "@/cowork/report-prompt"
import type { PromptInputProps } from "@/components/prompt-input/contracts"
import { requestFilesImport } from "@/cowork/files-import-navigation"
import { normalizePromptHistoryEntry, promptLength, type PromptHistoryComment } from "@/components/prompt-input/history"
import { createPersistedPromptInputHistory } from "@/components/prompt-input/history-store"
import { promptDesignPlaceholder, promptPlaceholder } from "@/components/prompt-input/placeholder"
import { createPromptSubmit, type FollowupDraft } from "@/components/prompt-input/submit"
import { selectionFromLines, type SelectedLineRange, useFile } from "@/context/file"
import { useComments } from "@/context/comments"
import { useCommand } from "@/context/command"
import { useLanguage } from "@/context/language"
import { useLayout } from "@/context/layout"
import { useLocal } from "@/context/local"
import { usePermission } from "@/context/permission"
import { type ImageAttachmentPart, usePrompt } from "@/context/prompt"
import { usePlatform } from "@/context/platform"
import { useSDK } from "@/context/sdk"
import { useServerSDK } from "@/context/server-sdk"
import { ServerConnection } from "@/context/server"
import { useTabs, type SessionTab } from "@/context/tabs"
import { useSync } from "@/context/sync"
import { createSessionTabs } from "@/pages/session/helpers"
import { showToast } from "@/utils/toast"
import { PromptInputV2, type PromptInputV2Suggestion } from "@opencode-ai/session-ui/v2/prompt-input"
import {
  createPromptInputV2Controller,
  createPromptInputV2State,
  type PromptInputV2Interaction,
} from "@opencode-ai/session-ui/v2/prompt-input/interaction"

export type PromptInputV2ComposerProps = {
  class?: string
  controller: PromptInputV2ComposerController
  borderUnderlay?: boolean
}

export type PromptInputV2ControllerProps = Omit<PromptInputProps, "class" | "submission">
export type PromptInputV2ComposerController = PromptInputV2Interaction & {
  readonly model: PromptInputProps["controls"]["model"]
  readonly coworkUploads: Array<{ id: string; name: string; status: "uploading" | "uploaded" | "failed" }>
  importCoworkCloudFiles(providerKey: string, connectionID: string, files: CoworkCloudFile[]): Promise<void>
  runCoworkReportTemplate(visibleContent: string, instructions: string): void
}

export function PromptInputV2Composer(props: PromptInputV2ComposerProps) {
  const dialog = useDialog()
  const command = useCommand()
  const language = useLanguage()
  return (
    <div class="flex flex-col gap-3">
      <Show when={coworkAgentMode() && props.controller.coworkUploads.length > 0}>
        <div class="flex flex-wrap gap-2 rounded-lg border border-v2-border-border-base bg-v2-background-bg-layer-01 p-2">
          <div class="w-full text-12-medium text-v2-text-text-muted">{language.t("cowork.files.panel.title")}</div>
          {props.controller.coworkUploads.map((file) => (
            <div class="flex items-center gap-2 rounded-md bg-v2-background-bg-base px-2 py-1 text-12-regular text-v2-text-text-muted">
              <Icon name="workspace" size="small" />
              <span class="max-w-[220px] truncate">{file.name}</span>
              <span>{language.t(`cowork.prompt.upload.${file.status}`)}</span>
            </div>
          ))}
        </div>
      </Show>
      <PromptInputV2
        controller={props.controller}
        borderUnderlay={props.borderUnderlay}
        class={props.class}
        variantControlVisible={!coworkAgentMode() && !props.controller.model.loading}
        attachKeybind={command.keybindParts("file.attach")}
        attachShortcut={command.keybind("file.attach")}
        attachLabel={coworkAgentMode() ? language.t("cowork.prompt.uploadFiles") : undefined}
        onAttach={coworkAgentMode() ? requestFilesImport : undefined}
        addMenuActionsVisible={!coworkAgentMode()}
        agentControlExtra={
          coworkAgentMode() ? (
            <CoworkAgentSettingsButton onRunTemplate={props.controller.runCoworkReportTemplate} />
          ) : undefined
        }
        nativeControls={coworkAgentMode()}
        nativeIntegrationsLabel={coworkAgentMode() ? language.t("cowork.integrations.menu") : undefined}
        onNativeIntegrations={
          coworkAgentMode()
            ? () => dialog.show(() => <DialogCoworkIntegrations onImport={props.controller.importCoworkCloudFiles} />)
            : undefined
        }
        extraAddMenuActions={
          coworkAgentMode() ? (
            <MenuV2.Item
              onSelect={() =>
                dialog.show(() => <DialogCoworkIntegrations onImport={props.controller.importCoworkCloudFiles} />)
              }
            >
              {language.t("cowork.integrations.menu")}
            </MenuV2.Item>
          ) : undefined
        }
        modelControl={
          coworkAgentMode() ? undefined : (
            <PromptInputV2ModelControl
              loading={props.controller.model.loading}
              paid={props.controller.model.paid}
              title={language.t("command.model.choose")}
              keybind={command.keybindParts("model.choose")}
              model={props.controller.model.selection}
              providerID={props.controller.model.selection.current()?.provider?.id}
              modelName={props.controller.model.selection.current()?.name ?? language.t("dialog.model.select.title")}
              onClose={props.controller.restoreFocus}
              onUnpaidClick={() =>
                dialog.show(() => <DialogSelectModelUnpaidV2 model={props.controller.model.selection} />)
              }
            />
          )
        }
      />
    </div>
  )
}

const coworkImportProviders = [
  { key: "google-drive", label: "Google Drive", logoPath: "/template-logos/google-drive.svg" },
  { key: "one-drive", label: "OneDrive", logoPath: "/template-logos/one-drive.svg" },
  { key: "sharepoint-online", label: "SharePoint", logoPath: "/template-logos/sharepoint-online.svg" },
] as const

function CoworkAgentSettingsButton(props: { onRunTemplate: (visibleContent: string, instructions: string) => void }) {
  const dialog = useDialog()
  const language = useLanguage()
  const [store, setStore] = createStore({ open: false, left: 0, bottom: 0 })
  onMount(() => {
    const close = (event: PointerEvent) => {
      if ((event.target as HTMLElement).closest("[data-cowork-agent-settings]")) return
      setStore("open", false)
    }
    document.addEventListener("pointerdown", close)
    onCleanup(() => document.removeEventListener("pointerdown", close))
  })
  const toggle = (event: MouseEvent) => {
    const target = event.currentTarget
    if (!(target instanceof HTMLElement)) return
    const rect = target.getBoundingClientRect()
    setStore({ open: !store.open, left: rect.left, bottom: window.innerHeight - rect.top + 8 })
  }
  return (
    <div data-cowork-agent-settings>
      <button
        type="button"
        class="flex h-8 items-center justify-center rounded-md px-2 text-v2-text-text-muted hover:bg-v2-background-bg-layer-02 hover:text-v2-text-text-base"
        title={language.t("cowork.agent.settings.title")}
        aria-label={language.t("cowork.agent.settings.title")}
        onClick={toggle}
      >
        <Icon name="settings-gear" size="small" />
      </button>
      <Show when={store.open}>
        <Portal>
          <div
            data-cowork-agent-settings
            class="fixed z-[2147483000] min-w-48 overflow-hidden rounded-lg border border-v2-border-border-base bg-v2-background-bg-layer-01 py-1 shadow-[var(--v2-elevation-raised)]"
            style={{ left: `${store.left}px`, bottom: `${store.bottom}px` }}
          >
            <button
              type="button"
              class="block w-full px-3 py-2 text-left text-[12px] text-v2-text-text-base hover:bg-v2-overlay-simple-overlay-hover"
              onClick={() => {
                setStore("open", false)
                dialog.show(() => <CoworkAgentSettingsDialog />)
              }}
            >
              {language.t("cowork.agent.settings.menu.reportLanguage")}
            </button>
            <button
              type="button"
              class="block w-full px-3 py-2 text-left text-[12px] text-v2-text-text-base hover:bg-v2-overlay-simple-overlay-hover"
              onClick={() => {
                setStore("open", false)
                dialog.show(() => <CoworkReportTemplateDialog onGenerate={props.onRunTemplate} />)
              }}
            >
              {language.t("cowork.agent.settings.menu.reportTemplate")}
            </button>
          </div>
        </Portal>
      </Show>
    </div>
  )
}

function CoworkAgentSettingsDialog() {
  const language = useLanguage()
  const dialog = useDialog()
  const [store, setStore] = createStore({ reportLanguage: storedCoworkReportOutputLanguage() })
  const selectLanguage = (value: CoworkReportOutputLanguage) => {
    setStore("reportLanguage", value)
    setStoredCoworkReportOutputLanguage(value)
  }
  return (
    <DialogV2 fit containerClass="!h-auto max-h-[calc(100vh_-_16px)] !w-[min(calc(100vw_-_16px),560px)]">
      <DialogHeader closeLabel={language.t("common.close")}>
        <DialogTitle>{language.t("cowork.agent.settings.title")}</DialogTitle>
      </DialogHeader>
      <DialogBody class="flex-none gap-0 p-4">
        <div class="mb-3">
          <h3 class="text-14-medium text-v2-text-text-base">
            {language.t("cowork.agent.settings.reportLanguage.title")}
          </h3>
          <p class="mt-1 text-12-regular text-v2-text-text-muted">
            {language.t("cowork.agent.settings.reportLanguage.description")}
          </p>
        </div>
        <div class="grid gap-2">
          <For each={coworkReportOutputLanguages}>
            {(value) => (
              <button
                type="button"
                class="flex items-center justify-between rounded-lg border border-v2-border-border-base bg-v2-background-bg-layer-01 px-3 py-2 text-left hover:bg-v2-background-bg-layer-02"
                classList={{
                  "border-v2-border-border-muted bg-v2-background-bg-layer-03": store.reportLanguage === value,
                }}
                onClick={() => selectLanguage(value)}
              >
                <span class="text-13-medium text-v2-text-text-base">
                  {language.t(`cowork.agent.settings.reportLanguage.option.${value}`)}
                </span>
                <Show when={store.reportLanguage === value}>
                  <Icon name="check" size="small" class="text-v2-text-text-base" />
                </Show>
              </button>
            )}
          </For>
        </div>
        <div class="mt-4 flex justify-end">
          <ButtonV2 variant="contrast" size="small" onClick={() => dialog.close()}>
            {language.t("common.close")}
          </ButtonV2>
        </div>
      </DialogBody>
    </DialogV2>
  )
}

function CoworkReportTemplateDialog(props: { onGenerate: (visibleContent: string, instructions: string) => void }) {
  const language = useLanguage()
  const dialog = useDialog()
  const generate = (template: CoworkReportTemplate, accountCard?: import("@/cowork/report-templates").AccountCardSelection) => {
    const prompt = buildCoworkReportTemplatePrompt(template, language.t, accountCard)
    dialog.close()
    window.setTimeout(() => props.onGenerate(prompt.visiblePrompt, prompt.instructions), 0)
  }
  return (
    <DialogV2
      fit
      rootClass="cowork-report-template-dialog-root"
      class="cowork-report-template-modal"
      containerClass="!h-auto max-h-[calc(100vh_-_16px)] !w-[min(calc(100vw_-_16px),1240px)]"
    >
      <DialogHeader closeLabel={language.t("common.close")}>
        <DialogTitle>{language.t("cowork.agent.settings.templates.title")}</DialogTitle>
      </DialogHeader>
      <DialogBody class="min-h-0 gap-0 bg-v2-background-bg-layer-02 p-0">
        <div class="border-b border-v2-border-border-base bg-v2-background-bg-base px-5 py-4">
          <p class="max-w-3xl text-13-regular leading-5 text-v2-text-text-muted">
            {language.t("cowork.agent.settings.templates.description")}
          </p>
        </div>
        <div class="max-h-[min(72vh,780px)] overflow-y-auto p-5">
          <CoworkReportTemplateCatalog onGenerate={generate} />
        </div>
      </DialogBody>
    </DialogV2>
  )
}

function DialogCoworkIntegrations(props: {
  onImport: (providerKey: string, connectionID: string, files: CoworkCloudFile[]) => Promise<void>
}) {
  const language = useLanguage()
  const dialog = useDialog()
  const [store, setStore] = createStore({
    providerKey: "",
    connectionID: "",
    search: "",
    folderStack: [] as Array<{ id: string; name: string }>,
    selected: {} as Record<string, CoworkCloudFile | undefined>,
    importing: false,
  })
  const [connections] = createResource(() => listIntegrationConnections())
  const availableProviders = createMemo(() =>
    coworkImportProviders.filter((provider) =>
      (connections()?.connections ?? []).some(
        (connection) => integrationConnectionProviderKey(connection) === provider.key,
      ),
    ),
  )
  const activeProvider = createMemo(() => store.providerKey || availableProviders()[0]?.key || "")
  const activeConnection = createMemo(
    () =>
      (connections()?.connections ?? []).find(
        (connection) => integrationConnectionProviderKey(connection) === activeProvider(),
      ) as CoworkIntegrationConnection | undefined,
  )
  const activeFolder = createMemo(() => store.folderStack[store.folderStack.length - 1])
  const [cloudFiles] = createResource(
    () => ({
      providerKey: activeProvider(),
      connectionID: activeConnection()?.connection_id,
      folderID: activeFolder()?.id,
    }),
    (input) =>
      input.connectionID
        ? listIntegrationFiles({
            providerKey: input.providerKey,
            connectionID: input.connectionID,
            folderID: input.folderID,
          })
        : Promise.resolve({ items: [], next_cursor: null }),
  )
  const displayedCloudFiles = createMemo(() => {
    const query = store.search.trim().toLowerCase()
    const items = cloudFiles()?.items ?? []
    if (!query) return items
    return items.filter((file) => `${file.name} ${file.mime_type ?? ""}`.toLowerCase().includes(query))
  })
  const selectedFiles = createMemo(() =>
    Object.values(store.selected).filter((file): file is CoworkCloudFile => !!file),
  )
  const importSelected = () => {
    const connection = activeConnection()
    if (!connection || selectedFiles().length === 0) return
    setStore("importing", true)
    void props
      .onImport(activeProvider(), connection.connection_id, selectedFiles())
      .then(() => dialog.close())
      .finally(() => setStore("importing", false))
  }
  return (
    <DialogV2
      fit
      rootClass="cowork-integrations-dialog-root"
      class="cowork-integrations-modal"
      containerClass="!h-auto max-h-[calc(100vh_-_16px)] !w-[min(calc(100vw_-_16px),900px)]"
    >
      <DialogHeader closeLabel={language.t("common.close")}>
        <DialogTitle>{language.t("cowork.integrations.title")}</DialogTitle>
      </DialogHeader>
      <DialogBody class="min-h-0 flex-none gap-0 p-4">
        <p class="mb-4 text-13-regular text-v2-text-text-muted">
          {language.t("cowork.integrations.import.description")}
        </p>
        <Show
          when={!connections.loading}
          fallback={
            <div class="rounded-lg border border-v2-border-border-base bg-v2-background-bg-layer-01 p-4 text-13-regular text-v2-text-text-muted">
              {language.t("common.loading")}
            </div>
          }
        >
          <Show
            when={availableProviders().length > 0}
            fallback={
              <div class="rounded-lg border border-v2-border-border-base bg-v2-background-bg-layer-01 p-6 text-center text-13-regular text-v2-text-text-muted">
                {language.t("cowork.integrations.import.empty")}
              </div>
            }
          >
            <div class="mb-4">
              <div class="mb-2 text-12-medium uppercase text-v2-text-text-muted">
                {language.t("cowork.integrations.import.connectedSources")}
              </div>
              <div class="grid gap-3 sm:grid-cols-3">
                <For each={availableProviders()}>
                  {(provider) => (
                    <button
                      type="button"
                      class="flex min-h-[92px] items-start gap-3 rounded-xl border border-v2-border-border-base bg-v2-background-bg-layer-01 p-3 text-left hover:bg-v2-background-bg-layer-02"
                      classList={{
                        "border-v2-border-border-muted bg-v2-background-bg-layer-03": activeProvider() === provider.key,
                      }}
                      onClick={() =>
                        setStore({ providerKey: provider.key, connectionID: "", folderStack: [], selected: {} })
                      }
                    >
                      <span class="flex size-10 shrink-0 items-center justify-center rounded-lg bg-white p-2">
                        <img src={provider.logoPath} alt="" class="max-h-full max-w-full" />
                      </span>
                      <span class="min-w-0">
                        <span class="block truncate text-14-medium text-v2-text-text-base">{provider.label}</span>
                        <span class="mt-1 block text-12-regular text-v2-text-text-muted">
                          {language.t("cowork.integrations.connected")}
                        </span>
                      </span>
                    </button>
                  )}
                </For>
              </div>
            </div>
            <div class="mb-2 flex items-center justify-between gap-3">
              <div class="text-12-medium uppercase text-v2-text-text-muted">
                {language.t("cowork.integrations.import.browseFiles")}
              </div>
              <div class="text-12-regular text-v2-text-text-muted">
                {language.t("cowork.integrations.import.selectedCount", { count: selectedFiles().length })}
              </div>
            </div>
            <label class="relative mb-4 block max-w-[420px]">
              <input
                type="search"
                value={store.search}
                onInput={(event) => setStore("search", event.currentTarget.value)}
                placeholder={language.t("common.search.placeholder")}
                aria-label={language.t("common.search.placeholder")}
                class="h-9 w-full rounded-full border border-v2-border-border-base bg-v2-background-bg-layer-01 px-4 pr-10 text-14-regular text-v2-text-text-base outline-none placeholder:text-v2-text-text-muted focus:border-v2-border-border-muted"
              />
              <Icon
                name="magnifying-glass"
                size="large"
                class="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-v2-text-text-muted"
              />
            </label>
            <div class="mb-3 flex flex-wrap items-center gap-2 text-12-medium text-v2-text-text-muted">
              <button
                type="button"
                class="rounded-md px-2 py-1 hover:bg-v2-background-bg-layer-02 hover:text-v2-text-text-base"
                onClick={() => setStore({ folderStack: [], selected: {} })}
              >
                {language.t("cowork.integrations.import.root")}
              </button>
              <For each={store.folderStack}>
                {(folder, index) => (
                  <>
                    <span>/</span>
                    <button
                      type="button"
                      class="max-w-[180px] truncate rounded-md px-2 py-1 hover:bg-v2-background-bg-layer-02 hover:text-v2-text-text-base"
                      onClick={() => setStore({ folderStack: store.folderStack.slice(0, index() + 1), selected: {} })}
                    >
                      {folder.name}
                    </button>
                  </>
                )}
              </For>
            </div>
            <div class="h-[390px] overflow-y-scroll rounded-lg border border-v2-border-border-base pr-2 [scrollbar-color:var(--v2-border-border-base)_transparent] [scrollbar-gutter:stable] [scrollbar-width:thin]">
              <For
                each={displayedCloudFiles()}
                fallback={
                  <div class="py-8 text-center text-13-regular text-v2-text-text-muted">
                    {cloudFiles.loading
                      ? language.t("common.loading")
                      : language.t("cowork.integrations.import.noFiles")}
                  </div>
                }
              >
                {(file) => (
                  <button
                    type="button"
                    class="flex w-full items-center gap-3 border-b border-v2-border-border-base/60 p-3 text-left hover:bg-v2-background-bg-layer-02"
                    onClick={() =>
                      file.is_folder
                        ? setStore({
                            search: "",
                            folderStack: [...store.folderStack, { id: file.id, name: file.name }],
                            selected: {},
                          })
                        : setStore("selected", file.id, store.selected[file.id] ? undefined : file)
                    }
                  >
                    <span class="flex size-9 shrink-0 items-center justify-center rounded-md border border-v2-border-border-base bg-v2-background-bg-base">
                      <Show
                        when={file.is_folder}
                        fallback={<FileIcon node={{ path: file.name, type: "file" }} class="size-4 shrink-0" />}
                      >
                        <Icon name="folder" size="normal" />
                      </Show>
                    </span>
                    <span class="min-w-0 flex-1">
                      <span class="block truncate text-13-medium text-v2-text-text-base">{file.name}</span>
                      <span class="block truncate text-12-regular text-v2-text-text-muted">
                        {file.is_folder
                          ? language.t("cowork.integrations.import.folder")
                          : (file.mime_type ?? language.t("cowork.integrations.import.file"))}
                      </span>
                    </span>
                    <Show when={!file.is_folder}>
                      <span
                        class="size-4 rounded border border-v2-border-border-base"
                        classList={{ "bg-v2-text-text-base": !!store.selected[file.id] }}
                      />
                    </Show>
                  </button>
                )}
              </For>
            </div>
            <div class="mt-4 flex items-center justify-between border-t border-v2-border-border-base pt-3">
              <span class="text-13-regular text-v2-text-text-muted">
                {language.t("cowork.integrations.import.selectedCount", { count: selectedFiles().length })}
              </span>
              <ButtonV2
                variant="contrast"
                size="small"
                disabled={selectedFiles().length === 0 || store.importing}
                onClick={importSelected}
              >
                {store.importing
                  ? language.t("cowork.integrations.import.importing")
                  : language.t("cowork.integrations.import.selected", { count: selectedFiles().length })}
              </ButtonV2>
            </div>
          </Show>
        </Show>
      </DialogBody>
    </DialogV2>
  )
}

function integrationConnectionProviderKey(connection: {
  provider_config_key?: string | null
  provider_key?: string | null
  provider?: string | null
}) {
  return normalizedIntegrationProviderKey(
    connection.provider_key || connection.provider || connection.provider_config_key,
  )
}

function normalizedIntegrationProviderKey(providerKey: string | null | undefined) {
  const normalized = providerKey?.trim().toLowerCase()
  if (normalized === "sharepoint") return "sharepoint-online"
  if (normalized === "gdrive") return "google-drive"
  if (normalized === "onedrive") return "one-drive"
  return normalized
}

export function usePromptInputV2Controller(props: PromptInputV2ControllerProps): PromptInputV2ComposerController {
  const sdk = useSDK()
  const serverSDK = useServerSDK()
  const appTabs = useTabs()
  const local = useLocal()
  const sync = useSync()
  const files = useFile()
  const layout = useLayout()
  const comments = useComments()
  const dialog = useDialog()
  const command = useCommand()
  const permission = usePermission()
  const language = useLanguage()
  const platform = usePlatform()
  const prompt = props.state ?? usePrompt()
  const reportTab = (): SessionTab | undefined =>
    props.controls.session.id
      ? { type: "session", server: ServerConnection.key(serverSDK().server), sessionId: props.controls.session.id }
      : undefined
  const reportMemory = createMemo(() => {
    const tab = reportTab()
    return tab ? reportTemplateMemory(appTabs, tab) : undefined
  })
  const [reportReadiness, setReportReadiness] = createStore({ expired: "" })
  createEffect(
    on(
      () => {
        const action = reportMemory()?.state.action
        return action?.status === "pending" ? action.workflowRunID : undefined
      },
      (id) => {
        setReportReadiness("expired", "")
        if (!id) return
        const timer = window.setTimeout(() => setReportReadiness("expired", id), 30_000)
        onCleanup(() => window.clearTimeout(timer))
      },
    ),
  )
  const [coworkUploads, setCoworkUploads] = createStore<
    Array<{ id: string; name: string; status: "uploading" | "uploaded" | "failed" }>
  >([])
  const [coworkContext] = createResource(
    () => (coworkAgentMode() && props.controls.session.id ? props.controls.session.id : undefined),
    (id) => getSessionContextByRemote(id, reportMemory()?.state.action?.expected?.companyID),
  )
  const [workspaceAgents] = createResource(
    () => (coworkAgentMode() ? "workspace-agents" : undefined),
    listWorkspaceAgents,
  )
  const reportSubmissions = new WeakMap<
    FollowupDraft,
    { memory: ReturnType<typeof reportTemplateMemory>; action: ReportTemplateAction }
  >()
  const settleReport = (draft: FollowupDraft, status: "failed" | "sent" | "queued") => {
    const submitted = reportSubmissions.get(draft)
    if (!submitted || submitted.memory.state.action?.workflowRunID !== submitted.action.workflowRunID) return
    submitted.memory.setState("action", { sending: false, status })
    reportSubmissions.delete(draft)
  }
  let editor: HTMLDivElement | undefined

  const interaction = createPromptInputV2State()
  const mode = () => interaction[0].mode
  const history = props.history ?? createPersistedPromptInputHistory()
  const tabs = () => props.controls.session.tabs
  const activeFileTab = createSessionTabs({
    tabs,
    pathFromTab: files.pathFromTab,
    normalizeTab: (tab) => (tab.startsWith("file://") ? files.tab(tab) : tab),
  }).activeFileTab
  const recent = createMemo(() => {
    const all = tabs().all()
    const active = activeFileTab()
    const order = active ? [active, ...all.filter((tab) => tab !== active)] : all
    return order.reduce<string[]>((result, tab) => {
      const path = files.pathFromTab(tab)
      if (!path || result.includes(path)) return result
      return [...result, path]
    }, [])
  })
  const info = createMemo(() => (props.controls.session.id ? sync().session.get(props.controls.session.id) : undefined))
  const working = createMemo(() => sync().data.session_working(props.controls.session.id ?? ""))
  const attachments = createMemo(() =>
    prompt.current().filter((part): part is ImageAttachmentPart => part.type === "image"),
  )
  const commentCount = createMemo(() => {
    if (mode() === "shell") return 0
    return prompt.context.items().filter((item) => !!item.comment?.trim()).length
  })
  const blank = createMemo(() => {
    const text = prompt
      .current()
      .map((part) => ("content" in part ? part.content : ""))
      .join("")
    return text.trim().length === 0 && attachments().length === 0 && commentCount() === 0
  })
  const stopping = createMemo(() => working() && blank())
  const placeholder = createMemo(() =>
    promptPlaceholder({
      mode: mode(),
      commentCount: commentCount(),
      example: mode() === "shell" ? "git status" : "",
      suggest: false,
      t: (key, params) => language.t(key as Parameters<typeof language.t>[0], params as never),
    }),
  )
  const designPlaceholder = () =>
    coworkAgentMode()
      ? language.t("cowork.prompt.placeholder")
      : promptDesignPlaceholder(mode(), placeholder(), (key, params) =>
          language.t(key as Parameters<typeof language.t>[0], params as never),
        )

  const historyComments = () => {
    const byID = new Map(comments.all().map((item) => [`${item.file}\n${item.id}`, item] as const))
    return prompt.context.items().flatMap((item) => {
      const comment = item.comment?.trim()
      if (!comment) return []
      const selection = item.commentID ? byID.get(`${item.path}\n${item.commentID}`)?.selection : undefined
      const nextSelection =
        selection ??
        (item.selection
          ? ({ start: item.selection.startLine, end: item.selection.endLine } satisfies SelectedLineRange)
          : undefined)
      if (!nextSelection) return []
      return [
        {
          id: item.commentID ?? item.key,
          path: item.path,
          selection: { ...nextSelection },
          comment,
          time: item.commentID ? (byID.get(`${item.path}\n${item.commentID}`)?.time ?? Date.now()) : Date.now(),
          origin: item.commentOrigin,
          preview: item.preview,
        } satisfies PromptHistoryComment,
      ]
    })
  }
  const restoreHistoryComments = (items: PromptHistoryComment[]) => {
    comments.replace(
      items.map((item) => ({
        id: item.id,
        file: item.path,
        selection: { ...item.selection },
        comment: item.comment,
        time: item.time,
      })),
    )
    prompt.context.replaceComments(
      items.map((item) => ({
        type: "file",
        path: item.path,
        selection: selectionFromLines(item.selection),
        comment: item.comment,
        commentID: item.id,
        commentOrigin: item.origin,
        preview: item.preview,
      })),
    )
  }

  const accepting = createMemo(() => {
    const id = props.controls.session.id
    if (!id) return permission.isAutoAcceptingDirectory(sdk().directory)
    return permission.isAutoAccepting(id, sdk().directory)
  })
  const submission = createPromptSubmit({
    prompt,
    info,
    imageAttachments: attachments,
    commentCount,
    autoAccept: accepting,
    mode,
    working,
    editor: () => editor,
    queueScroll: () => requestAnimationFrame(() => editor?.scrollIntoView({ block: "nearest" })),
    promptLength,
    addToHistory: (value, mode) => controller.addHistory(value, mode),
    resetHistoryNavigation: () => controller.resetHistory(),
    setMode: (next) => controller.dispatch({ type: next === "shell" ? "mode.shell" : "mode.normal" }),
    setPopover: (popover) => {
      if (!popover) controller.dispatch({ type: "popover.close" })
    },
    newSessionWorktree: () => props.newSessionWorktree,
    onNewSessionWorktreeReset: props.onNewSessionWorktreeReset,
    shouldQueue: props.shouldQueue,
    onQueue: props.onQueue,
    onAbort: props.onAbort,
    onSubmit: () => {
      setCoworkUploads([])
      props.onSubmit?.()
    },
    transformDraft: injectCoworkContext,
    onDraftError: (draft) => settleReport(draft, "failed"),
    onDraftSettled: (draft, outcome) => settleReport(draft, outcome === "cancelled" ? "failed" : outcome),
    model: props.controls.model.selection,
  })

  const referenceDescription = (reference: ReferenceInfo) =>
    reference.source.type === "git" ? reference.source.repository : reference.source.path
  const references = createMemo(() =>
    sync()
      .data.reference.filter((reference) => !reference.hidden && !hiddenCoworkReference(reference.name))
      .map((reference) => ({
        id: `reference:${reference.name}`,
        kind: "reference" as const,
        label: `@${reference.name}`,
        path: reference.path,
        description: reference.description ?? referenceDescription(reference),
        mention: {
          type: "file" as const,
          path: reference.path,
          content: `@${reference.name}`,
          start: 0,
          end: 0,
          mime: "application/x-directory",
          filename: reference.name,
        },
      })),
  )
  const resources = createMemo(() =>
    Object.values(sync().data.mcp_resource).map((resource) => ({
      id: `resource:${resource.server}:${resource.uri}`,
      kind: "resource" as const,
      label: `@${resource.name}`,
      path: resource.uri,
      description: resource.description,
      mention: {
        type: "file" as const,
        path: resource.uri,
        content: `@${resource.name}`,
        start: 0,
        end: 0,
        mime: resource.mimeType ?? "text/plain",
        filename: resource.name,
        url: resource.uri,
        source: {
          type: "resource" as const,
          text: { value: `@${resource.name}`, start: 0, end: resource.name.length + 1 },
          clientName: resource.server,
          uri: resource.uri,
        },
      },
      resource,
    })),
  )
  const context = createMemo<PromptInputV2Suggestion[]>(() => [
    ...references(),
    ...props.controls.agents.available
      .filter((agent) => !agent.hidden && agent.mode !== "primary" && !hiddenCoworkMentionAgent(agent.name))
      .map((agent) => ({
        id: `agent:${agent.name}`,
        kind: "agent" as const,
        label: `@${agent.name}`,
        mention: { type: "agent" as const, name: agent.name, content: `@${agent.name}`, start: 0, end: 0 },
      })),
    ...resources(),
    ...recent().map((path) => ({
      id: `file:${path}`,
      kind: "file" as const,
      label: path,
      path,
      recent: true,
      mention: { type: "file" as const, path, content: `@${path}`, start: 0, end: 0 },
    })),
  ])
  const slashCommands = createMemo(() => [
    ...sync().data.command.map((item) => ({
      id: `custom.${item.name}`,
      trigger: item.name,
      title: item.name,
      description: item.description,
      type: "custom" as const,
    })),
    ...command.options
      .filter((item) => !item.disabled && !item.id.startsWith("suggested.") && item.slash)
      .map((item) => ({
        id: item.id,
        trigger: item.slash!,
        title: item.title,
        description: item.description,
        type: "builtin" as const,
      })),
  ])
  const commands = createMemo<PromptInputV2Suggestion[]>(() =>
    slashCommands().map((item) => ({
      id: item.id,
      kind: "command",
      label: `/${item.trigger}`,
      trigger: item.trigger,
      title: item.title,
      description: item.description,
      keybind: command.keybindParts(item.id),
    })),
  )
  const variants = createMemo(() => ["default", ...props.controls.model.selection.variant.list()])
  const coworkAgentOptions = createMemo(() => {
    const runtimeOptions = props.controls.agents.options.filter((name: string) => !hiddenCoworkAgent(name))
    if (!coworkAgentMode()) return runtimeOptions
    const context =
      coworkContext.state === "ready" || coworkContext.state === "refreshing" ? coworkContext.latest : undefined
    const agents =
      workspaceAgents.state === "ready" || workspaceAgents.state === "refreshing" ? workspaceAgents.latest : undefined
    if (!context || context.remote_opencode_session_id !== props.controls.session.id || !agents) return []
    const options = activeRuntimeAgentOptions(runtimeOptions, agents, context)
    const selected =
      runtimeAgentOptionFromMetadata(runtimeOptions, agents, context.session_metadata) ??
      runtimeAgentOption(runtimeOptions, agents, storedDefaultAgentID())
    return selected && !options.includes(selected) ? [selected, ...options] : options
  })
  const controller = createPromptInputV2Controller({
    store: () => prompt.capture().store,
    state: interaction,
    identity: () => prompt.capture(),
    history: {
      entries: (mode) =>
        history.entries(mode).map((value) => {
          const entry = normalizePromptHistoryEntry(value)
          return { prompt: entry.prompt, metadata: entry.comments }
        }),
      add: (value, mode) => history.add(value, mode, mode === "shell" ? [] : historyComments()),
      capture: historyComments,
      restore: (metadata) => restoreHistoryComments(metadata as PromptHistoryComment[]),
    },
    commands,
    context,
    searchContextFiles: async (query) =>
      (await files.searchFilesAndDirectories(query)).map((path) => ({
        id: `file:${path}`,
        kind: "file",
        label: path,
        path,
        mention: { type: "file", path, content: `@${path}`, start: 0, end: 0 },
      })),
    onContextRemove(item) {
      if (item?.commentID) comments.remove(item.path, item.commentID)
    },
    openAttachment: (attachment) =>
      dialog.show(() => <ImagePreview src={attachment.blob.url} alt={attachment.filename} />),
    onAddAttachments: uploadCoworkAttachments,
    openContext(key) {
      const item = controller.contextItem(key)
      if (item) openComment(item, props, sync, layout, files, comments)
    },
    onEditor(element) {
      editor = element as HTMLDivElement
      props.ref?.(editor)
    },
    onSuggestionSelect(item) {
      if (item.kind !== "command") return
      const selected = slashCommands().find((entry) => entry.id === item.id)
      if (!selected || selected.type === "custom") return
      return () => command.trigger(selected.id, "slash")
    },
    attachments: {
      picker: platform.openAttachmentPickerDialog,
      directory: () => sdk().directory,
      isDialogActive: () => !!dialog.active,
      warn: () =>
        showToast({
          title: language.t("prompt.toast.pasteUnsupported.title"),
          description: language.t("prompt.toast.pasteUnsupported.description"),
        }),
      duplicate: () => showToast({ title: language.t("prompt.toast.attachmentDuplicate.title") }),
      onError: (error) =>
        showToast({
          variant: "error",
          title: language.t("common.requestFailed"),
          description: error instanceof Error ? error.message : String(error),
        }),
      readClipboardImage: platform.readClipboardImage,
      getPathForFile: platform.getPathForFile,
      store: platform.draftStore?.putBlob,
    },
    view: {
      placeholder: designPlaceholder,
      get agent() {
        const options = coworkAgentOptions()
        return props.controls.agents.visible && options.length > 0
          ? {
              options: () => options.map((name) => ({ id: name, label: name })),
              current: () => props.controls.agents.current,
              onSelect: (value: string) => props.controls.agents.select(value),
              keybind: () => command.keybindParts("agent.cycle"),
            }
          : undefined
      },
      variant: {
        options: () => variants().map((value) => ({ id: value, label: value })),
        current: () => props.controls.model.selection.variant.current() ?? "default",
        onSelect: (value) => props.controls.model.selection.variant.set(value === "default" ? undefined : value),
        keybind: () => command.keybindParts("model.variant.cycle"),
      },
      submit: {
        stopping,
        working,
        onSubmit: () => void submission.handleSubmit(new Event("submit")),
        onStop: () => void submission.abort(),
      },
    },
  })
  Object.defineProperty(controller, "model", { get: () => props.controls.model })

  onMount(() => {
    const exportReport = (event: Event) => {
      if (!coworkAgentMode()) return
      const promptText = (event as CustomEvent<{ prompt?: string }>).detail?.prompt?.trim()
      if (!promptText) return
      prompt.set([{ type: "text", content: promptText, start: 0, end: promptText.length }], promptText.length)
      queueMicrotask(() => controller.submit())
    }
    window.addEventListener("cowork:report-export", exportReport)
    onCleanup(() => window.removeEventListener("cowork:report-export", exportReport))
  })

  command.register("prompt-input", () => [
    {
      id: "file.attach",
      title: language.t("prompt.action.attachFile"),
      category: language.t("command.category.file"),
      keybind: "mod+u",
      disabled: controller.state.mode !== "normal",
      onSelect: () => controller.attach(),
    },
    {
      id: "prompt.mode.shell",
      title: language.t("command.prompt.mode.shell"),
      category: language.t("command.category.session"),
      keybind: "mod+shift+x",
      disabled: controller.state.mode === "shell",
      onSelect: () => controller.dispatch({ type: "mode.shell" }),
    },
    {
      id: "prompt.mode.normal",
      title: language.t("command.prompt.mode.normal"),
      category: language.t("command.category.session"),
      keybind: "mod+shift+e",
      disabled: controller.state.mode === "normal",
      onSelect: () => controller.dispatch({ type: "mode.normal" }),
    },
  ])

  createEffect(
    on(
      () => props.edit?.id,
      (id) => {
        const edit = props.edit
        if (!id || !edit) return
        prompt.context.items().forEach((item) => prompt.context.remove(item.key))
        edit.context.forEach((item) =>
          prompt.context.add({
            type: item.type,
            path: item.path,
            selection: item.selection,
            comment: item.comment,
            commentID: item.commentID,
            commentOrigin: item.commentOrigin,
            preview: item.preview,
          }),
        )
        controller.dispatch({ type: "mode.normal" })
        controller.resetHistory()
        prompt.set(edit.prompt, promptLength(edit.prompt))
        controller.restoreFocus()
        props.onEditLoaded?.()
      },
      { defer: true },
    ),
  )

  const reportFailure = (model = false) =>
    showToast({
      title: language.t(model ? "prompt.toast.modelAgentRequired.title" : "prompt.toast.promptSendFailed.title"),
      description: language.t(model ? "prompt.toast.modelAgentRequired.description" : "common.requestFailed"),
    })
  const runCoworkReportTemplate = (visibleContent: string, instructions: string, handoff?: ReportTemplateAction) => {
    const memory = reportMemory()
    if (memory?.state.action?.sending) return
    if (memory && !handoff) {
      const context = coworkContext.latest
      memory.setState(
        "action",
        createReportTemplateAction({
          visiblePrompt: visibleContent,
          instructions,
          reportOutputLanguage: coworkReportOutputLanguageInstructionValue(language.locale()),
          request: undefined,
          expected: context
            ? {
                companyID: context.company_id,
                projectID: context.project_id,
                periodID: context.period_id ?? context.workflow_id ?? null,
                sessionID: context.session_id,
              }
            : undefined,
        }),
      )
    }
    prompt.set([{ type: "text", content: visibleContent, start: 0, end: visibleContent.length }], visibleContent.length)
    if (!memory || mode() !== "normal" || props.edit) {
      reportFailure()
      return
    }
    // Capture and submit synchronously, before navigation can retarget this composer.
    void submission.handleSubmit(new Event("submit"))
  }

  createEffect(() => {
    if (!coworkAgentMode()) return
    const memory = reportMemory()
    if (!memory || (memory.state.action?.status !== "pending" && !memory.state.action?.restoreDraft)) return
    const expired = reportReadiness.expired === memory.state.action.workflowRunID
    const ready = {
      expired,
      prompt: prompt.ready(),
      loading:
        !expired &&
        (!info() ||
          props.controls.model.loading ||
          !props.controls.model.selection.ready() ||
          !local.session.ready() ||
          props.controls.agents.loading ||
          !sync().ready ||
          coworkContext.loading ||
          workspaceAgents.loading),
      model: !!props.controls.model.selection.current(),
      agent: !!local.agent.current(),
      session: !!info() && !expired,
      blocked:
        mode() !== "normal" ||
        !!props.edit ||
        !!info()?.parentID ||
        working() ||
        attachments().length > 0 ||
        commentCount() > 0,
      text: prompt
        .current()
        .map((part) => ("content" in part ? part.content : ""))
        .join(""),
    }
    untrack(() =>
      advanceReportTemplate(memory, ready, {
        stage: (text) => prompt.set([{ type: "text", content: text, start: 0, end: text.length }], text.length),
        run: (action) => runCoworkReportTemplate(action.visiblePrompt, action.instructions, action),
        fail: (model) =>
          expired
            ? showToast({
                title: language.t("prompt.toast.promptSendFailed.title"),
                description: language.t("cowork.newChat.timeout"),
              })
            : reportFailure(model),
      }),
    )
  })

  return Object.assign(controller, {
    coworkUploads,
    importCoworkCloudFiles,
    runCoworkReportTemplate,
  }) as PromptInputV2ComposerController

  async function injectCoworkContext(
    draft: Parameters<NonNullable<Parameters<typeof createPromptSubmit>[0]["transformDraft"]>>[0],
  ) {
    const memory = reportMemory()
    const action = memory && matchingReportTemplate(memory, draft)
    const scope = sdk().scope
    const target = prompt.capture()
    if (!coworkAgentMode() && !action) return draft
    if (action && memory) {
      if (memory.state.disposed || action.sending || props.edit || mode() !== "normal") {
        throw new Error(language.t("common.requestFailed"))
      }
      memory.setState("action", { sending: true, status: "claimed" })
      reportSubmissions.set(draft, { memory, action })
    }
    const result = await injectReportTemplateContext({
      draft,
      action,
      reportOutputLanguage: coworkReportOutputLanguageInstructionValue(language.locale()),
      getContext: getSessionContextByRemote,
      manifest: coworkFileManifest,
      error: () => new Error(language.t("common.requestFailed")),
    })
    if (
      action &&
      (memory?.state.disposed ||
        sdk().scope !== scope ||
        props.controls.session.id !== draft.sessionID ||
        prompt.capture() !== target ||
        target.current() !== draft.prompt)
    ) {
      throw new Error(language.t("common.requestFailed"))
    }
    return result
  }

  async function coworkFileManifest(projectID: string, workflowID?: string, companyID?: string, strict = false) {
    const files = await listFiles(projectID, workflowID, companyID)
    return Promise.all(
      files.slice(0, 10).map(async (file) => {
        const detail = await getFile(projectID, file.id, companyID).catch((error) => {
          if (strict) throw error
          return undefined
        })
        return {
          file_id: file.id,
          filename: file.filename,
          content_type: file.content_type,
          metadata: file.parsed_metadata,
          parts: (detail?.parts ?? []).slice(0, 25).map((part) => ({
            name: part.name,
            part_type: part.part_type,
            preview_text: (part.preview_text ?? "").slice(0, 2500),
            metadata: part.part_metadata,
          })),
        }
      }),
    )
  }

  async function uploadCoworkAttachments(files: File[]) {
    if (!coworkAgentMode() || !props.controls.session.id || files.length === 0) return
    const context = await getSessionContextByRemote(props.controls.session.id)
    if (!context) return false
    const entries = files.map((file) => ({ id: crypto.randomUUID(), name: file.name, status: "uploading" as const }))
    setCoworkUploads(entries)
    await Promise.allSettled(
      files.map((file, index) =>
        uploadFile(context.project_id, file, context.workflow_id ?? undefined, context.session_id, context.company_id)
          .then((uploaded) => {
            setCoworkUploads(index, "id", uploaded.id)
            setCoworkUploads(index, "status", "uploaded")
          })
          .catch(() => {
            setCoworkUploads(index, "status", "failed")
          }),
      ),
    )
    window.dispatchEvent(new CustomEvent("cowork-files-uploaded", { detail: { sessionID: props.controls.session.id } }))
    return []
  }

  async function importCoworkCloudFiles(providerKey: string, connectionID: string, files: CoworkCloudFile[]) {
    if (!coworkAgentMode() || !props.controls.session.id || files.length === 0) return
    const context = await getSessionContextByRemote(props.controls.session.id)
    if (!context) return
    const entries = files.map((file) => ({ id: file.id, name: file.name, status: "uploading" as const }))
    setCoworkUploads(entries)
    await importIntegrationFiles({
      projectID: context.project_id,
      workflowID: context.workflow_id,
      sessionID: context.session_id,
      providerKey,
      connectionID,
      files,
    })
      .then((response) => {
        entries.forEach((entry, index) => {
          setCoworkUploads(index, "id", response.results[index]?.file_id ?? entry.id)
          setCoworkUploads(index, "status", response.results[index]?.status === "imported" ? "uploaded" : "failed")
        })
        window.dispatchEvent(
          new CustomEvent("cowork-files-uploaded", { detail: { sessionID: props.controls.session.id } }),
        )
      })
      .catch(() => entries.forEach((_, index) => setCoworkUploads(index, "status", "failed")))
  }
}

function hiddenCoworkAgent(name: string) {
  if (!coworkAgentMode()) return false
  const normalized = name.toLowerCase()
  return normalized === "plan" || normalized === "build"
}

function hiddenCoworkMentionAgent(name: string) {
  if (!coworkAgentMode()) return false
  return true
}

function hiddenCoworkReference(name: string) {
  if (!coworkAgentMode()) return false
  const normalized = name.toLowerCase()
  return normalized === "explore" || normalized === "general"
}

function PromptInputV2ModelControl(props: {
  loading: boolean
  paid: boolean
  title: string
  keybind: string[]
  model: PromptInputV2ComposerController["model"]["selection"]
  providerID?: string
  modelName: string
  onClose: () => void
  onUnpaidClick: () => void
}) {
  const shouldAnimate = createMemo<boolean>((previous) => previous ?? props.loading)
  const content = () => (
    <>
      <Show when={props.providerID}>
        {(providerID) => (
          <ProviderIcon
            id={providerID()}
            class="size-4 shrink-0 opacity-40 group-hover:opacity-100 transition-opacity duration-150"
            style={{ "will-change": "opacity", transform: "translateZ(0)" }}
          />
        )}
      </Show>
      <span class="truncate leading-4">{props.modelName}</span>
      <span class="-ml-0.5 -mr-1 flex shrink-0">
        <Icon name="chevron-down" />
      </span>
    </>
  )
  return (
    <Show when={!props.loading}>
      <TooltipV2
        placement="top"
        gutter={4}
        value={
          <>
            {props.title}
            <KeybindV2 keys={props.keybind} variant="neutral" />
          </>
        }
      >
        <Show
          when={props.paid}
          fallback={
            <ButtonV2
              data-action="prompt-model"
              data-control-type="dialog"
              variant="ghost-muted"
              size="normal"
              class="min-w-0 max-w-[220px] justify-start ![font-weight:440] group"
              classList={{ "animate-in fade-in": shouldAnimate() }}
              style={{ height: "28px" }}
              onClick={props.onUnpaidClick}
            >
              {content()}
            </ButtonV2>
          }
        >
          <ModelSelectorPopoverV2
            model={props.model}
            trigger={(triggerProps) => (
              <ButtonV2
                {...triggerProps}
                variant="ghost-muted"
                size="normal"
                style={{ height: "28px" }}
                class="min-w-0 max-w-[220px] justify-start ![font-weight:440] group"
                classList={{ "animate-in fade-in": shouldAnimate() }}
                data-action="prompt-model"
                data-control-type="popover"
              >
                {content()}
              </ButtonV2>
            )}
            onClose={props.onClose}
          />
        </Show>
      </TooltipV2>
    </Show>
  )
}

function openComment(
  item: { path: string; commentID?: string; commentOrigin?: "review" | "file" },
  props: PromptInputV2ControllerProps,
  sync: ReturnType<typeof useSync>,
  layout: ReturnType<typeof useLayout>,
  files: ReturnType<typeof useFile>,
  comments: ReturnType<typeof useComments>,
) {
  if (!item.commentID) return
  const focus = { file: item.path, id: item.commentID }
  comments.setActive(focus)
  const queueFocus = (attempts = 6) => {
    requestAnimationFrame(() => {
      comments.setFocus({ ...focus })
      if (attempts <= 0) return
      requestAnimationFrame(() => {
        const current = comments.focus()
        if (current?.file === focus.file && current.id === focus.id) queueFocus(attempts - 1)
      })
    })
  }
  const diffs = props.controls.session.id ? sync().data.session_diff[props.controls.session.id] : undefined
  const review =
    item.commentOrigin === "review" || (item.commentOrigin !== "file" && diffs?.some((diff) => diff.file === item.path))
  if (!props.controls.session.reviewPanel.opened()) props.controls.session.reviewPanel.open()
  if (review) {
    layout.fileTree.setTab("changes")
    props.controls.session.tabs.setActive("review")
    queueFocus()
    return
  }
  layout.fileTree.setTab("all")
  const tab = files.tab(item.path)
  void props.controls.session.tabs.open(tab)
  props.controls.session.tabs.setActive(tab)
  void Promise.resolve(files.load(item.path)).finally(() => queueFocus())
}
